import type { FragmentType } from '@e-socna/data-layer';
import { GroupingModeEnum } from '@e-socna/data-layer';

import { flattenServiceUsagesGroups } from '../../utils/flatten-service-usages-groups.util';

import type { ManualTotalForm_ResolvableServiceValue_Fragment } from '../../forms/ManualTotal/fragments/manual-total-value.fragment';

import { ServiceUsagesGroupsLayout } from './GroupsLayout';
import type {
  ServiceUsagesGroupsLayoutProps,
  ServiceUsagesLayoutGroup,
} from './GroupsLayout';
import { ServiceUsages } from '.';
import type { ListProps, ServiceUsagesType } from './types/props.type';

export interface ServiceUsagesDisplayGroup extends ServiceUsagesLayoutGroup {
  readonly manualTotal?: FragmentType<
    typeof ManualTotalForm_ResolvableServiceValue_Fragment
  > | null;
  readonly serviceUsages: ServiceUsagesType<true>;
}

export const isInManualGroupingMode = (
  groups:
    readonly Readonly<{ groupingMode?: GroupingModeEnum }>[] | null | undefined,
) => !!groups?.some((group) => group.groupingMode === GroupingModeEnum.Manual);

type Props = Omit<
  ListProps<true>,
  'fallbackServiceUsages' | 'manualTotal' | 'serviceUsages'
> &
  ServiceUsagesGroupsLayoutProps &
  Readonly<{
    fallbackServiceUsagesGroups?: readonly ServiceUsagesDisplayGroup[] | null;
    serviceUsagesGroups?: readonly ServiceUsagesDisplayGroup[] | null;
  }>;

// The groups of a live pricing, inheriting the ones of its type until they are
// specified.
export const ServiceUsagesGroups = ({
  additionalTotalGroups,
  fallbackServiceUsagesGroups,
  groupSectionLabel,
  groupTotalCalculations,
  leadingContent,
  optionGroupsSelection,
  selectedOptionGroupIds,
  serviceUsagesGroups,
  showInheritanceAlert = true,
  totalCalculation,
  totalSections,
  ...listProps
}: Readonly<Props>) => {
  const fallbackServiceUsages = flattenServiceUsagesGroups(
    fallbackServiceUsagesGroups,
  );

  const displayedGroups = serviceUsagesGroups ?? fallbackServiceUsagesGroups;

  if (!displayedGroups?.length) {
    return (
      <>
        {leadingContent}
        <ServiceUsages<true>
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...listProps}
          fallbackServiceUsages={fallbackServiceUsages}
          serviceUsages={flattenServiceUsagesGroups(serviceUsagesGroups)}
          showInheritanceAlert={showInheritanceAlert}
          totalCalculation={totalCalculation}
        />
      </>
    );
  }

  return (
    <ServiceUsagesGroupsLayout
      additionalTotalGroups={additionalTotalGroups}
      groupSectionLabel={groupSectionLabel}
      groupTotalCalculations={groupTotalCalculations}
      groups={displayedGroups}
      leadingContent={leadingContent}
      optionGroupsSelection={optionGroupsSelection}
      // eslint-disable-next-line react/jsx-no-bind
      renderGroupUsages={(group, slots) => (
        <ServiceUsages<true>
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...listProps}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...slots}
          fallbackServiceUsages={
            serviceUsagesGroups ? fallbackServiceUsages : group.serviceUsages
          }
          manualTotal={group.manualTotal}
          serviceUsages={serviceUsagesGroups ? group.serviceUsages : undefined}
        />
      )}
      selectedOptionGroupIds={selectedOptionGroupIds}
      showInheritanceAlert={showInheritanceAlert}
      totalCalculation={totalCalculation}
      totalSections={totalSections}
    />
  );
};
